import React, { FC, useState } from "react";
import { Alert, Badge, Button, Card, Col, Row, Spinner } from "react-bootstrap";

import { AiGoalType, DailyUserSummaryType } from "../../../../DTO";
import { AlignmentSection } from "./alignment-section";
import { GoalCard } from "./goal-card";
import { TrafficLight } from "../../../../components";
import { formatDateTime, prettifyKey } from "./summary-helpers";

type DailySummarySectionProps = {
    hasWeekSelection: boolean;
    hasDaySelection: boolean;
    selectedDayId: number;
    loading: boolean;
    error: string;
    summary?: DailyUserSummaryType;
    onRetry: () => void;
    dayGoal?: AiGoalType | null;
    onGoalUpdated?: (goal: AiGoalType) => void;
};

const PRIMARY_NUTRITION_KEYS = ["calories", "protein", "carbohydrates", "fat"];
const SECONDARY_NUTRITION_KEYS = [
    "fiber",
    "sugar",
    "sodium",
    "cholesterol",
    "saturated_fat",
    "trans_fat",
];

const getNutritionUnit = (key: string) => {
    if (key === "calories") return "kcal";
    if (key === "sodium" || key === "cholesterol") return "mg";
    return "g";
};

const toSafePercent = (value: number, max: number) => {
    if (!max) return 0;
    return Math.max(0, Math.min(100, (value / max) * 100));
};

const NUTRITION_ABBREV: Record<string, string> = {
    calories: "Cal",
    protein: "Pro",
    carbohydrates: "Carb",
    fat: "Fat",
    fiber: "Fiber",
    sugar: "Sugar",
    sodium: "Na",
    cholesterol: "Chol",
    saturated_fat: "Sat.F",
    trans_fat: "Trans",
};

const NUTRITION_UNIT: Record<string, string> = {
    calories: "kcal",
    sodium: "mg",
    cholesterol: "mg",
};

const nutUnit = (k: string) => NUTRITION_UNIT[k] ?? "g";
const nutAbbrev = (k: string) => NUTRITION_ABBREV[k] ?? prettifyKey(k);

export const DailySummarySection: FC<DailySummarySectionProps> = ({
    hasWeekSelection,
    hasDaySelection,
    selectedDayId,
    loading,
    error,
    summary,
    onRetry,
    dayGoal,
    onGoalUpdated,
}) => {
    const [innerTab, setInnerTab] = useState<
        "overview" | "food-logs" | "nutrition"
    >("overview");

    return (
        <Card className="summary-panel summary-panel-content shadow-sm border-0">
            <Card.Body className="p-3 p-md-4">
                <div className="summary-section-head mb-3">
                    <div>
                        <div className="summary-caption">Daily View</div>
                        <h2 className="h5 mb-0">Daily Summary</h2>
                    </div>
                </div>

                {!hasWeekSelection && (
                    <Alert variant="light" className="mb-0">
                        Select a week first.
                    </Alert>
                )}

                {hasWeekSelection && !hasDaySelection && (
                    <Alert variant="light" className="mb-0">
                        Select a day to load summary.
                    </Alert>
                )}

                {selectedDayId && loading && (
                    <div className="d-flex align-items-center gap-2">
                        <Spinner size="sm" animation="border" />
                        <span className="small text-muted">
                            Loading daily summary...
                        </span>
                    </div>
                )}

                {selectedDayId && !loading && error && (
                    <Alert variant="danger" className="mb-0">
                        <div className="mb-2">{error}</div>
                        <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={onRetry}
                            aria-label="Retry daily summary"
                        >
                            Retry
                        </Button>
                    </Alert>
                )}

                {selectedDayId &&
                    !loading &&
                    !error &&
                    summary &&
                    !summary.has_summary && (
                        <Alert variant="light" className="mb-0">
                            No daily summary found for selected day.
                        </Alert>
                    )}

                {selectedDayId &&
                    !loading &&
                    !error &&
                    summary?.has_summary && (
                        <div className="d-flex flex-column gap-3">
                            <div
                                className="summary-inner-tabs"
                                role="tablist"
                                aria-label="Daily content tabs"
                            >
                                {(
                                    [
                                        "overview",
                                        "food-logs",
                                        "nutrition",
                                    ] as const
                                ).map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        role="tab"
                                        aria-selected={innerTab === tab}
                                        className={`summary-inner-tab-btn ${innerTab === tab ? "summary-inner-tab-btn-active" : ""}`}
                                        onClick={() => setInnerTab(tab)}
                                    >
                                        {tab === "food-logs"
                                            ? `Food Logs${(summary.food_logs?.length ?? 0) > 0 ? ` (${summary.food_logs.length})` : ""}`
                                            : tab.charAt(0).toUpperCase() +
                                              tab.slice(1)}
                                    </button>
                                ))}
                            </div>

                            {innerTab === "overview" && (
                                <>
                                    <div className="summary-headline">
                                        <div>
                                            <div className="summary-title">
                                                {summary.display.emoji}{" "}
                                                {summary.display.title}
                                            </div>
                                            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                                                <TrafficLight
                                                    status={
                                                        summary.display.light
                                                    }
                                                    compact
                                                    label="Daily rating"
                                                />
                                                <span className="small text-muted">
                                                    Generated{" "}
                                                    {formatDateTime(
                                                        summary.generated_at,
                                                    )}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="summary-score text-end">
                                            <div>
                                                {summary.score.percentage}%
                                            </div>
                                            <div className="summary-score-sub">
                                                {summary.score.total}/
                                                {summary.score.max}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="summary-kpi-grid">
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Status
                                            </div>
                                            <div className="summary-kpi-value text-capitalize">
                                                <TrafficLight
                                                    status={
                                                        summary.display.light
                                                    }
                                                    label=""
                                                />
                                            </div>
                                        </div>
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Worst Category
                                            </div>
                                            <div className="summary-kpi-value text-capitalize">
                                                {summary.insights
                                                    .worst_category || "-"}
                                            </div>
                                        </div>
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Meals Logged
                                            </div>
                                            <div className="summary-kpi-value">
                                                {summary.food_logs.length}
                                            </div>
                                        </div>
                                        <div className="summary-kpi-tile">
                                            <div className="summary-tile-label">
                                                Protein Bonus
                                            </div>
                                            <div className="summary-kpi-value">
                                                {summary.protein_bonus.earned
                                                    ? `+${summary.protein_bonus.points}`
                                                    : "Not earned"}
                                            </div>
                                            <div className="summary-kpi-sub">
                                                {
                                                    summary.protein_bonus
                                                        .high_protein_meals
                                                }
                                                /
                                                {
                                                    summary.protein_bonus
                                                        .required_meals
                                                }{" "}
                                                meals
                                            </div>
                                        </div>
                                    </div>

                                    <Row className="g-3">
                                        <Col xl={7}>
                                            <div className="summary-tile h-100">
                                                <div className="summary-tile-label mb-2">
                                                    Scores Breakdown
                                                </div>
                                                <div className="summary-category-grid">
                                                    {Object.entries(
                                                        summary.scores_breakdown,
                                                    ).map(([key, value]) => (
                                                        <div
                                                            className="summary-category-tile"
                                                            key={key}
                                                        >
                                                            <div className="d-flex justify-content-between align-items-center gap-2">
                                                                <span className="text-capitalize fw-semibold">
                                                                    {key}
                                                                </span>
                                                                <TrafficLight
                                                                    status={
                                                                        value.light
                                                                    }
                                                                    compact
                                                                    label=""
                                                                />
                                                            </div>
                                                            <div className="summary-category-score">
                                                                {value.points}
                                                                <span>
                                                                    /{value.max}
                                                                </span>
                                                            </div>
                                                            <div
                                                                className="summary-mini-bar"
                                                                aria-hidden="true"
                                                            >
                                                                <span
                                                                    style={{
                                                                        width: `${toSafePercent(
                                                                            value.points,
                                                                            value.max,
                                                                        )}%`,
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </Col>

                                        <Col xl={5}>
                                            <div className="summary-tile h-100">
                                                <div className="summary-tile-label mb-2">
                                                    Guidance
                                                </div>
                                                <div className="summary-stack-sm">
                                                    <div className="summary-mini-block">
                                                        <div className="summary-mini-label">
                                                            Today Insight
                                                        </div>
                                                        <div className="small">
                                                            {summary.insights
                                                                .daily_insight ||
                                                                "-"}
                                                        </div>
                                                    </div>
                                                    <div className="summary-mini-block">
                                                        <div className="summary-mini-label">
                                                            Tomorrow Focus
                                                        </div>
                                                        <div className="small text-muted">
                                                            {summary.insights
                                                                .tomorrow_focus ||
                                                                "-"}
                                                        </div>
                                                    </div>
                                                    {summary.suggestions
                                                        .length > 0 && (
                                                        <div className="summary-mini-block">
                                                            <div className="summary-mini-label">
                                                                Suggestions
                                                            </div>
                                                            <div className="d-flex flex-column gap-1">
                                                                {summary.suggestions.map(
                                                                    (
                                                                        item,
                                                                        index,
                                                                    ) => (
                                                                        <div
                                                                            key={`daily-suggestion-${index}`}
                                                                            className="summary-focus-item"
                                                                        >
                                                                            {
                                                                                item
                                                                            }
                                                                        </div>
                                                                    ),
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <details className="summary-details">
                                                        <summary className="pointer">
                                                            AI Summary
                                                        </summary>
                                                        <div className="small mt-2">
                                                            {summary.ai_summary ||
                                                                "-"}
                                                        </div>
                                                    </details>
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>

                                    {summary.alignment_summary && (
                                        <AlignmentSection
                                            title="Daily Alignment Summary"
                                            alignment={
                                                summary.alignment_summary
                                            }
                                        />
                                    )}
                                </>
                            )}

                            {innerTab === "food-logs" && (
                                <div>
                                    {(summary.food_logs?.length ?? 0) === 0 ? (
                                        <Alert variant="light" className="mb-0">
                                            No food logs recorded for this day.
                                        </Alert>
                                    ) : (
                                        <div className="d-flex flex-column gap-3">
                                            {(summary.food_logs ?? []).map(
                                                (foodLog) => {
                                                    const nut =
                                                        foodLog.total_nutrition;
                                                    const tl =
                                                        foodLog.traffic_light;
                                                    return (
                                                        <div
                                                            key={`daily-food-log-${foodLog.id}`}
                                                            className="summary-food-log"
                                                        >
                                                            {/* ── Header ── */}
                                                            <div className="summary-food-log-header">
                                                                <div className="d-flex flex-column gap-1">
                                                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                                                        <span
                                                                            className="fw-bold text-capitalize"
                                                                            style={{
                                                                                fontSize:
                                                                                    "0.92rem",
                                                                            }}
                                                                        >
                                                                            {
                                                                                foodLog.type
                                                                            }
                                                                            {foodLog.is_side
                                                                                ? " · Side"
                                                                                : ""}
                                                                        </span>
                                                                        <Badge
                                                                            bg="secondary"
                                                                            style={{
                                                                                fontWeight: 500,
                                                                            }}
                                                                        >
                                                                            {
                                                                                foodLog.status
                                                                            }
                                                                        </Badge>
                                                                        {tl
                                                                            ?.overall
                                                                            ?.light && (
                                                                            <TrafficLight
                                                                                status={
                                                                                    tl
                                                                                        .overall
                                                                                        .light
                                                                                }
                                                                                compact
                                                                                label={`${tl.overall.score} points`}
                                                                            />
                                                                        )}
                                                                    </div>
                                                                    <div
                                                                        className="d-flex gap-3 flex-wrap"
                                                                        style={{
                                                                            fontSize:
                                                                                "0.75rem",
                                                                            color: "var(--sum-muted)",
                                                                        }}
                                                                    >
                                                                        <span>
                                                                            {formatDateTime(
                                                                                foodLog.created_at,
                                                                            )}
                                                                        </span>
                                                                        {foodLog.duration_time >
                                                                            0 && (
                                                                            <span>
                                                                                Duration:{" "}
                                                                                {
                                                                                    foodLog.duration_time
                                                                                }{" "}
                                                                                min
                                                                            </span>
                                                                        )}
                                                                        {foodLog.on_time !==
                                                                            undefined && (
                                                                            <span>
                                                                                On
                                                                                time:{" "}
                                                                                {foodLog.on_time
                                                                                    ? "Yes"
                                                                                    : "No"}
                                                                            </span>
                                                                        )}
                                                                        {foodLog.after_midnight && (
                                                                            <span
                                                                                style={{
                                                                                    color: "#dc2626",
                                                                                }}
                                                                            >
                                                                                After
                                                                                midnight
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                {tl?.priority_action && (
                                                                    <div
                                                                        className="summary-priority-action"
                                                                        style={{
                                                                            maxWidth: 220,
                                                                        }}
                                                                    >
                                                                        <div className="summary-priority-action-label mb-1">
                                                                            Priority
                                                                            Action
                                                                        </div>
                                                                        <div
                                                                            style={{
                                                                                fontSize:
                                                                                    "0.78rem",
                                                                            }}
                                                                        >
                                                                            {
                                                                                tl.priority_action
                                                                            }
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </div>

                                                            <div className="summary-food-log-body">
                                                                {/* ── Behavioral ratings ── */}
                                                                <div>
                                                                    <div className="summary-section-divider">
                                                                        Behavioral
                                                                        Ratings
                                                                    </div>
                                                                    <div className="summary-behavioral-grid">
                                                                        <div className="summary-behavioral-cell">
                                                                            <div className="summary-behavioral-label">
                                                                                Healthiness
                                                                            </div>
                                                                            <div className="summary-behavioral-value">
                                                                                {foodLog.healthiness ??
                                                                                    "—"}{" "}
                                                                                /
                                                                                10
                                                                            </div>
                                                                        </div>
                                                                        <div className="summary-behavioral-cell">
                                                                            <div className="summary-behavioral-label">
                                                                                Hunger
                                                                                Level
                                                                            </div>
                                                                            <div className="summary-behavioral-value">
                                                                                {foodLog.hungry ??
                                                                                    "—"}{" "}
                                                                                /
                                                                                10
                                                                            </div>
                                                                        </div>
                                                                        <div className="summary-behavioral-cell">
                                                                            <div className="summary-behavioral-label">
                                                                                Want
                                                                                to
                                                                                Eat
                                                                            </div>
                                                                            <div className="summary-behavioral-value">
                                                                                {foodLog.want_to_eat ??
                                                                                    "—"}{" "}
                                                                                /
                                                                                10
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* ── Images ── */}
                                                                {foodLog
                                                                    .attachments
                                                                    ?.length >
                                                                    0 && (
                                                                    <div>
                                                                        <div className="summary-section-divider">
                                                                            Photos
                                                                            (
                                                                            {
                                                                                foodLog
                                                                                    .attachments
                                                                                    .length
                                                                            }
                                                                            )
                                                                        </div>
                                                                        <div className="summary-food-log-images">
                                                                            {foodLog.attachments.map(
                                                                                (
                                                                                    att,
                                                                                ) => (
                                                                                    <a
                                                                                        key={`att-${att.id}`}
                                                                                        href={
                                                                                            att.url
                                                                                        }
                                                                                        target="_blank"
                                                                                        rel="noreferrer"
                                                                                        className="summary-food-log-image-link"
                                                                                        title={
                                                                                            att.original_name ||
                                                                                            att.name ||
                                                                                            "Food image"
                                                                                        }
                                                                                    >
                                                                                        <img
                                                                                            src={
                                                                                                att.url
                                                                                            }
                                                                                            alt={
                                                                                                att.original_name ||
                                                                                                att.name ||
                                                                                                `Food log ${foodLog.id}`
                                                                                            }
                                                                                            className="summary-food-log-image"
                                                                                            loading="lazy"
                                                                                        />
                                                                                    </a>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* ── Food items with per-item nutrition ── */}
                                                                <div>
                                                                    <div className="summary-section-divider">
                                                                        Food
                                                                        Items (
                                                                        {foodLog
                                                                            .items
                                                                            ?.length ??
                                                                            0}
                                                                        )
                                                                    </div>
                                                                    {(foodLog
                                                                        .items
                                                                        ?.length ??
                                                                        0) ===
                                                                    0 ? (
                                                                        <div className="small text-muted">
                                                                            No
                                                                            detected
                                                                            food
                                                                            items.
                                                                        </div>
                                                                    ) : (
                                                                        <div className="summary-food-log-items">
                                                                            {foodLog.items.map(
                                                                                (
                                                                                    item,
                                                                                ) => (
                                                                                    <div
                                                                                        key={`item-${item.id}`}
                                                                                        className="summary-food-log-item"
                                                                                    >
                                                                                        <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                                                                                            <span className="summary-food-log-item-name">
                                                                                                {
                                                                                                    item.name
                                                                                                }
                                                                                                {item.description ? (
                                                                                                    <span className="fw-normal text-muted">
                                                                                                        {" "}
                                                                                                        —{" "}
                                                                                                        {
                                                                                                            item.description
                                                                                                        }
                                                                                                    </span>
                                                                                                ) : null}
                                                                                            </span>
                                                                                            <span className="summary-food-log-item-portion flex-shrink-0">
                                                                                                {item
                                                                                                    .portion
                                                                                                    ?.display ||
                                                                                                    "—"}
                                                                                            </span>
                                                                                        </div>
                                                                                        {item.nutrition && (
                                                                                            <div className="summary-food-log-item-nutrition">
                                                                                                {Object.keys(
                                                                                                    NUTRITION_ABBREV,
                                                                                                ).map(
                                                                                                    (
                                                                                                        k,
                                                                                                    ) => (
                                                                                                        <div
                                                                                                            key={
                                                                                                                k
                                                                                                            }
                                                                                                            className="summary-food-log-item-nut-cell"
                                                                                                        >
                                                                                                            <div className="summary-food-log-item-nut-label">
                                                                                                                {nutAbbrev(
                                                                                                                    k,
                                                                                                                )}
                                                                                                            </div>
                                                                                                            <div className="summary-food-log-item-nut-value">
                                                                                                                {String(
                                                                                                                    item
                                                                                                                        .nutrition[
                                                                                                                        k as keyof typeof item.nutrition
                                                                                                                    ] ??
                                                                                                                        0,
                                                                                                                )}
                                                                                                            </div>
                                                                                                            <div className="summary-food-log-item-nut-unit">
                                                                                                                {nutUnit(
                                                                                                                    k,
                                                                                                                )}
                                                                                                            </div>
                                                                                                        </div>
                                                                                                    ),
                                                                                                )}
                                                                                            </div>
                                                                                        )}
                                                                                    </div>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* ── Meal total nutrition ── */}
                                                                {nut && (
                                                                    <div>
                                                                        <div className="summary-section-divider">
                                                                            Meal
                                                                            Totals
                                                                        </div>
                                                                        <div className="summary-meal-totals">
                                                                            {Object.keys(
                                                                                NUTRITION_ABBREV,
                                                                            ).map(
                                                                                (
                                                                                    k,
                                                                                ) => (
                                                                                    <div
                                                                                        key={
                                                                                            k
                                                                                        }
                                                                                        className="summary-meal-total-cell"
                                                                                    >
                                                                                        <div className="summary-meal-total-label">
                                                                                            {nutAbbrev(
                                                                                                k,
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="summary-meal-total-value">
                                                                                            {String(
                                                                                                nut[
                                                                                                    k as keyof typeof nut
                                                                                                ] ??
                                                                                                    0,
                                                                                            )}
                                                                                        </div>
                                                                                        <div className="summary-meal-total-unit">
                                                                                            {nutUnit(
                                                                                                k,
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                ),
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                )}

                                                                {/* ── AI summary ── */}
                                                                {foodLog.ai_summary && (
                                                                    <details className="summary-details">
                                                                        <summary className="pointer">
                                                                            AI
                                                                            Log
                                                                            Summary
                                                                        </summary>
                                                                        <div className="small mt-2">
                                                                            {
                                                                                foodLog.ai_summary
                                                                            }
                                                                        </div>
                                                                    </details>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                },
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {innerTab === "nutrition" && (
                                <div className="summary-tile">
                                    <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                                        <div className="summary-tile-label mb-0">
                                            Nutrition Totals
                                        </div>
                                        <span className="small text-muted">
                                            Day totals
                                        </span>
                                    </div>
                                    <div className="summary-metric-grid">
                                        {PRIMARY_NUTRITION_KEYS.map((k) => (
                                            <div
                                                key={`primary-${k}`}
                                                className="summary-metric-tile"
                                            >
                                                <div className="summary-mini-label">
                                                    {prettifyKey(k)}
                                                </div>
                                                <div className="summary-metric-value">
                                                    {String(
                                                        summary
                                                            .nutrition_totals?.[
                                                            k
                                                        ] ?? 0,
                                                    )}
                                                </div>
                                                <div className="summary-metric-unit">
                                                    {getNutritionUnit(k)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="summary-subpanel mt-2">
                                        <div className="summary-mini-label mb-2">
                                            More Metrics
                                        </div>
                                        <div className="d-flex flex-column gap-1">
                                            {SECONDARY_NUTRITION_KEYS.map(
                                                (k) => (
                                                    <div
                                                        key={`daily-total-${k}`}
                                                        className="summary-key-row"
                                                    >
                                                        <span>
                                                            {prettifyKey(k)}
                                                        </span>
                                                        <span className="fw-semibold">
                                                            {String(
                                                                summary
                                                                    .nutrition_totals?.[
                                                                    k
                                                                ] ?? 0,
                                                            )}{" "}
                                                            {getNutritionUnit(
                                                                k,
                                                            )}
                                                        </span>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                {hasDaySelection && !loading && dayGoal && onGoalUpdated && (
                    <GoalCard goal={dayGoal} onUpdated={onGoalUpdated} />
                )}

                {hasDaySelection && !loading && !dayGoal && (
                    <p className="text-muted small mt-3 mb-0">
                        No goal generated for this day yet.
                    </p>
                )}
            </Card.Body>
        </Card>
    );
};
